import {
    useCallback,
    useEffect,
    useMemo,
    useState,
    type CSSProperties,
    type FormEvent,
    type ReactElement,
} from "react";
import { Save, Trash2 } from "lucide-react";
import { space } from "@tokens/index";
import {
    Button,
    IconButton,
    SkeletonRows,
    Stack,
    Text,
    TextInput,
    useViewport,
} from "@primitives/index";
import {
    AdminSection,
    AdminSelect,
    EmptyLine,
    ErrorAlert,
    Field,
    FormActions,
    MutationError,
} from "../admin-ui";
import {
    QUEUE_PRIORITY_STRATEGIES,
    useQueuePriorityPolicy,
    useUpdateQueuePriorityPolicy,
} from "../next-ranking";
import { useQueueTree } from "../queues";
import { flattenQueues, parseRank, strategyLabel } from "./policy";
import { ValidationNote } from "./ValidationNote";

// Policy row: queue name, strategy, rank, remove. `medium`+ keeps one line
// with fluid columns; `compact` stacks name + remove, then strategy, then
// rank.
const policyRowWideStyle: CSSProperties = {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.4fr) minmax(0, 1fr) minmax(8ch, 0.6fr) auto",
    alignItems: "center",
    gap: space[3],
};

const policyRowCompactStyle: CSSProperties = {
    display: "grid",
    gap: space[2],
    minWidth: 0,
};

interface DraftOverride {
    readonly queueId: string;
    readonly strategy: string;
    readonly rank: string;
}

interface StrategySelectProps {
    readonly value: string;
    readonly label: string;
    readonly onChange: (next: string) => void;
}

function StrategySelect({ value, label, onChange }: StrategySelectProps): ReactElement {
    return (
        <AdminSelect value={value} aria-label={label} onChange={(e) => onChange(e.target.value)}>
            {QUEUE_PRIORITY_STRATEGIES.map((s) => (
                <option key={s} value={s}>
                    {strategyLabel(s)}
                </option>
            ))}
        </AdminSelect>
    );
}

export function QueuePriorityPolicySection(): ReactElement {
    const { isCompact } = useViewport();
    const query = useQueuePriorityPolicy();
    const treeQuery = useQueueTree();
    const mutation = useUpdateQueuePriorityPolicy();
    const data = query.data;

    const [personalStrategy, setPersonalStrategy] = useState<string>("absolute");
    const [personalRank, setPersonalRank] = useState<string>("1");
    const [overrides, setOverrides] = useState<readonly DraftOverride[]>([]);
    const [addQueueId, setAddQueueId] = useState<string>("");

    useEffect(() => {
        if (data === undefined) return;
        setPersonalStrategy(data.personalQueueDefault.strategy);
        setPersonalRank(
            data.personalQueueDefault.absoluteRank > 0
                ? String(data.personalQueueDefault.absoluteRank)
                : "1",
        );
        setOverrides(
            data.overrides.map((o) => ({
                queueId: o.queueId,
                strategy: o.strategy,
                rank: o.absoluteRank > 0 ? String(o.absoluteRank) : "1",
            })),
        );
    }, [data]);

    const allQueues = useMemo(() => flattenQueues(treeQuery.data?.roots ?? []), [treeQuery.data]);
    const queueNameById = useMemo(() => {
        const map = new Map<string, string>();
        for (const q of allQueues) map.set(q.id, q.name);
        return map;
    }, [allQueues]);

    const overriddenIds = useMemo(() => new Set(overrides.map((o) => o.queueId)), [overrides]);
    const addableQueues = useMemo(
        () => allQueues.filter((q) => q.status === "active" && !overriddenIds.has(q.id)),
        [allQueues, overriddenIds],
    );

    // An absolute assignment needs a valid rank; a dynamic one ignores it.
    const personalInvalid = personalStrategy === "absolute" && parseRank(personalRank) === null;
    const anyOverrideInvalid = overrides.some(
        (o) => o.strategy === "absolute" && parseRank(o.rank) === null,
    );
    const anyInvalid = personalInvalid || anyOverrideInvalid;

    const handleAddOverride = useCallback((queueId: string) => {
        if (queueId.length === 0) return;
        setOverrides((prev) => [...prev, { queueId, strategy: "dynamic", rank: "1" }]);
        setAddQueueId("");
    }, []);

    const handleRemoveOverride = useCallback((queueId: string) => {
        setOverrides((prev) => prev.filter((o) => o.queueId !== queueId));
    }, []);

    const handleOverrideStrategy = useCallback((queueId: string, strategy: string) => {
        setOverrides((prev) => prev.map((o) => (o.queueId === queueId ? { ...o, strategy } : o)));
    }, []);

    const handleOverrideRank = useCallback((queueId: string, rank: string) => {
        setOverrides((prev) => prev.map((o) => (o.queueId === queueId ? { ...o, rank } : o)));
    }, []);

    const handleSave = useCallback(() => {
        if (anyInvalid) return;
        const personalRankValue =
            personalStrategy === "absolute" ? (parseRank(personalRank) ?? 1) : 0;
        mutation.mutate({
            personalQueueDefault: {
                strategy: personalStrategy,
                absoluteRank: personalRankValue,
            },
            overrides: overrides.map((o) => ({
                queueId: o.queueId,
                strategy: o.strategy,
                absoluteRank: o.strategy === "absolute" ? (parseRank(o.rank) ?? 1) : 0,
            })),
        });
    }, [anyInvalid, personalStrategy, personalRank, overrides, mutation]);

    const canSave = !anyInvalid && !mutation.isPending;

    // Enter in any rank input saves, through the form, when the draft is valid.
    const handleSubmit = useCallback(
        (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            if (canSave) handleSave();
        },
        [canSave, handleSave],
    );

    const personalRankInput = (
        <TextInput
            type="number"
            inputMode="numeric"
            min="1"
            step="1"
            fullWidth
            error={personalInvalid}
            value={personalRank}
            aria-label="Personal queue absolute rank"
            onChange={(e) => setPersonalRank(e.target.value)}
        />
    );

    return (
        <AdminSection
            title="Queue priority policy"
            description="Absolute queues occupy a hard top tier (lower rank wins) and out-rank any scored queue. Dynamic queues sit in the scored floor. Personal queues with no override use the personal default."
        >
            {query.isLoading && (
                <SkeletonRows rows={3} rowHeight={40} shape="card" label="Loading priority policy" />
            )}
            {query.error !== null && (
                <ErrorAlert onRetry={() => void query.refetch()}>
                    Couldn&rsquo;t load the priority policy. {query.error.message}
                </ErrorAlert>
            )}

            {data !== undefined && (
                <Stack direction="column" gap={5}>
                    <form onSubmit={handleSubmit} style={{ display: "contents" }}>
                    <Stack direction="column" gap={2}>
                        <Text as="h3" role="bodySm" weight="medium" tone="muted">
                            Personal queue default
                        </Text>
                        <div style={isCompact ? policyRowCompactStyle : policyRowWideStyle}>
                            <Text role="body" truncate>
                                All personal queues
                            </Text>
                            <StrategySelect
                                value={personalStrategy}
                                label="Personal queue default strategy"
                                onChange={setPersonalStrategy}
                            />
                            {personalStrategy === "absolute" ? (
                                personalRankInput
                            ) : isCompact ? null : (
                                <span />
                            )}
                        </div>
                    </Stack>

                    <Stack direction="column" gap={2}>
                        <Text as="h3" role="bodySm" weight="medium" tone="muted">
                            Per-queue overrides ({overrides.length})
                        </Text>
                        {overrides.length === 0 && (
                            <EmptyLine>
                                No overrides — every shared queue is scored dynamically.
                            </EmptyLine>
                        )}
                        {overrides.map((o) => {
                            const name = queueNameById.get(o.queueId) ?? o.queueId;
                            const invalid = o.strategy === "absolute" && parseRank(o.rank) === null;
                            const strategy = (
                                <StrategySelect
                                    value={o.strategy}
                                    label={`Strategy for ${name}`}
                                    onChange={(next) => handleOverrideStrategy(o.queueId, next)}
                                />
                            );
                            const rank =
                                o.strategy === "absolute" ? (
                                    <TextInput
                                        type="number"
                                        inputMode="numeric"
                                        min="1"
                                        step="1"
                                        fullWidth
                                        error={invalid}
                                        value={o.rank}
                                        aria-label={`Absolute rank for ${name}`}
                                        onChange={(e) =>
                                            handleOverrideRank(o.queueId, e.target.value)
                                        }
                                    />
                                ) : null;
                            const remove = (
                                <IconButton
                                    type="button"
                                    icon={Trash2}
                                    aria-label={`Remove override for ${name}`}
                                    tooltip={`Remove override for ${name}`}
                                    onClick={() => handleRemoveOverride(o.queueId)}
                                />
                            );
                            if (isCompact) {
                                return (
                                    <div key={o.queueId} style={policyRowCompactStyle}>
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                gap: space[2],
                                                minWidth: 0,
                                            }}
                                        >
                                            <Text role="body" weight="medium" truncate>
                                                {name}
                                            </Text>
                                            {remove}
                                        </div>
                                        {strategy}
                                        {rank}
                                    </div>
                                );
                            }
                            return (
                                <div key={o.queueId} style={policyRowWideStyle}>
                                    <Text role="body" truncate>
                                        {name}
                                    </Text>
                                    {strategy}
                                    {rank ?? <span />}
                                    {remove}
                                </div>
                            );
                        })}

                        <div style={{ maxWidth: isCompact ? "100%" : "40ch" }}>
                            <Field label="Add a queue override">
                                {(id) => (
                                    <AdminSelect
                                        id={id}
                                        value={addQueueId}
                                        disabled={addableQueues.length === 0}
                                        onChange={(e) => handleAddOverride(e.target.value)}
                                    >
                                        <option value="">
                                            {treeQuery.isLoading
                                                ? "Loading queues…"
                                                : addableQueues.length === 0
                                                  ? "Every queue already has an override"
                                                  : "Add an override for a queue…"}
                                        </option>
                                        {addableQueues.map((q) => (
                                            <option key={q.id} value={q.id}>
                                                {q.name}
                                                {q.isPersonal ? " (personal)" : ""}
                                            </option>
                                        ))}
                                    </AdminSelect>
                                )}
                            </Field>
                        </div>
                    </Stack>

                    {data.updatedAt !== null && (
                        <div>
                            <Text role="caption" tone="muted">
                                Last updated {new Date(data.updatedAt).toLocaleString()}
                                {data.updatedBy !== null && (
                                    <>
                                        {" by "}
                                        <Text role="mono" size="xs" tone="muted">
                                            {data.updatedBy}
                                        </Text>
                                    </>
                                )}
                            </Text>
                        </div>
                    )}

                    <MutationError error={mutation.error} />

                    <FormActions
                        note={
                            anyInvalid ? (
                                <ValidationNote>
                                    Absolute queues need a whole-number rank of 1 or more.
                                </ValidationNote>
                            ) : undefined
                        }
                        primary={
                            <Button type="submit" variant="primary" size="md" disabled={!canSave}>
                                <Save size={14} strokeWidth={2.25} aria-hidden="true" />
                                {mutation.isPending ? "Saving…" : "Save policy"}
                            </Button>
                        }
                    />
                    </form>
                </Stack>
            )}
        </AdminSection>
    );
}
